import { useMemo } from 'react';
import { SYNTAX_CLASS, type SyntaxToken } from '../../utils/syntaxColors';

interface JsonViewerProps {
  data: string;
}

interface Token {
  type: SyntaxToken;
  value: string;
}

function tokenizeJson(json: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < json.length) {
    const char = json[i];

    // Whitespace - preserve it
    if (/\s/.test(char)) {
      let whitespace = '';
      while (i < json.length && /\s/.test(json[i])) {
        whitespace += json[i];
        i++;
      }
      tokens.push({ type: 'punctuation', value: whitespace });
      continue;
    }

    // Punctuation
    if ('{}[],:'.includes(char)) {
      tokens.push({ type: 'punctuation', value: char });
      i++;
      continue;
    }

    // String (key or value)
    if (char === '"') {
      let str = '"';
      i++;
      while (i < json.length && json[i] !== '"') {
        if (json[i] === '\\' && i + 1 < json.length) {
          str += json[i] + json[i + 1];
          i += 2;
        } else {
          str += json[i];
          i++;
        }
      }
      str += '"';
      i++;

      // Look ahead to see if this is a key (followed by :)
      let lookahead = i;
      while (lookahead < json.length && /\s/.test(json[lookahead])) {
        lookahead++;
      }
      const isKey = json[lookahead] === ':';

      tokens.push({ type: isKey ? 'key' : 'string', value: str });
      continue;
    }

    // Number
    if (/[-\d]/.test(char)) {
      let num = '';
      while (i < json.length && /[-\d.eE+]/.test(json[i])) {
        num += json[i];
        i++;
      }
      tokens.push({ type: 'number', value: num });
      continue;
    }

    // true, false, null
    if (json.slice(i, i + 4) === 'true') {
      tokens.push({ type: 'boolean', value: 'true' });
      i += 4;
      continue;
    }
    if (json.slice(i, i + 5) === 'false') {
      tokens.push({ type: 'boolean', value: 'false' });
      i += 5;
      continue;
    }
    if (json.slice(i, i + 4) === 'null') {
      tokens.push({ type: 'null', value: 'null' });
      i += 4;
      continue;
    }

    // Unknown character - just add it
    tokens.push({ type: 'punctuation', value: char });
    i++;
  }

  return tokens;
}

export function JsonViewer({ data }: JsonViewerProps) {
  const tokens = useMemo(() => {
    if (!data || data === 'null') {
      return null;
    }

    // Try to parse and re-stringify for consistent formatting
    let formatted = data;
    try {
      const parsed = JSON.parse(data);
      formatted = JSON.stringify(parsed, null, 2);
    } catch {
      // If parse fails, use original string
    }

    return tokenizeJson(formatted);
  }, [data]);

  if (!tokens) {
    return <span className="text-base-content/70 italic">No data</span>;
  }

  return (
    <pre className="code-surface text-sm font-mono p-3 overflow-x-auto whitespace-pre-wrap wrap-break-word">
      {tokens.map((token, index) => (
        <span key={index} className={SYNTAX_CLASS[token.type]}>{token.value}</span>
      ))}
    </pre>
  );
}
